import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useParams } from "react-router";
import type { Element, ElementContent } from "hast";
import { AlertTriangle, Check, Copy, Info, Lightbulb, Link2, MessageSquareWarning, OctagonAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDocsBasePath } from "../../hooks/useDocs";
import type { DocsDiagram } from "../../models/Docs";
import DiagramFigure from "./DiagramFigure";
import { resolveDocsLink } from "./docsLinks";
import { AlertType, rehypeAlerts, rehypeDiagramIndex, rehypeHeadingIds } from "./rehypeDocs";

const ALERTS: Record<AlertType, { label: string; icon: React.ElementType; className: string }> = {
  note: { label: "Note", icon: Info, className: "border-sky-500/40 bg-sky-500/5 [&_.docs-alert-title]:text-sky-700 dark:[&_.docs-alert-title]:text-sky-300" },
  tip: { label: "Tip", icon: Lightbulb, className: "border-emerald-500/40 bg-emerald-500/5 [&_.docs-alert-title]:text-emerald-700 dark:[&_.docs-alert-title]:text-emerald-300" },
  important: { label: "Important", icon: MessageSquareWarning, className: "border-primary/40 bg-primary/5 [&_.docs-alert-title]:text-primary" },
  warning: { label: "Warning", icon: AlertTriangle, className: "border-amber-500/50 bg-amber-500/5 [&_.docs-alert-title]:text-amber-700 dark:[&_.docs-alert-title]:text-amber-300" },
  caution: { label: "Caution", icon: OctagonAlert, className: "border-destructive/50 bg-destructive/5 [&_.docs-alert-title]:text-destructive dark:[&_.docs-alert-title]:text-red-400" },
};

const textOf = (node: ElementContent): string =>
  node.type === "text" ? node.value : "children" in node ? node.children.map(textOf).join("") : "";

/**
 * A code block with a Copy button, for the long prompts, schemas and scripts SEs paste elsewhere. When the browser
 * refuses the clipboard, the block is selected instead so the reader's own copy shortcut takes it.
 */
const CodeBlock: React.FC<{ children: React.ReactNode; singleLine: boolean }> = ({ children, singleLine }) => {
  const preRef = useRef<HTMLPreElement>(null);
  const [state, setState] = useState<"idle" | "copied" | "selected">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 2500);
    return () => clearTimeout(timer);
  }, [state]);

  const copy = async () => {
    const pre = preRef.current;
    if (!pre) return;
    try {
      // Without the fence's closing line break, so a pasted command waits to be run instead of running at once.
      await navigator.clipboard.writeText((pre.textContent ?? "").replace(/\n$/, ""));
      setState("copied");
    } catch {
      const range = document.createRange();
      range.selectNodeContents(pre);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      setState("selected");
    }
  };

  return (
    <div className="relative">
      {/*
        The button never covers text. A one-line command keeps it beside the line, in room on the right; the line
        wraps rather than scrolls, since scrolled text would pass under the button. A longer block gets room above.
      */}
      <pre
        ref={preRef}
        className={singleLine ? "pr-24 whitespace-pre-wrap [overflow-wrap:anywhere] [&_code]:whitespace-pre-wrap" : "pt-10"}
      >
        {children}
      </pre>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={copy}
        className={cn("absolute right-2 h-7 gap-1 px-2 text-xs", singleLine ? "top-1/2 -translate-y-1/2" : "top-2")}
      >
        {state === "copied" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        <span aria-live="polite">
          {state === "copied" ? "Copied" : state === "selected" ? "Selected: press Ctrl+C or ⌘C" : "Copy"}
        </span>
      </Button>
    </div>
  );
};

interface MarkdownContentProps {
  markdown: string;
  /** Slug of the page the markdown belongs to; relative links resolve from its folder. */
  baseSlug: string;
  /** Anchors for the `##`–`####` headings, in document order, as computed by the server. */
  headingAnchors?: string[];
  /** The page's ```diagram blocks rendered by the server, in document order. */
  diagrams?: DocsDiagram[];
  className?: string;
}

const HeadingWithAnchor = (Tag: "h2" | "h3" | "h4") =>
  function Heading({ id, children }: { id?: string; children?: React.ReactNode }) {
    return (
      <Tag id={id} className="group scroll-mt-4">
        {children}
        {id && (
          <a
            href={`#${id}`}
            aria-label="Link to this section"
            className="ml-2 inline-flex align-middle text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 no-underline"
          >
            <Link2 className="size-4" />
          </a>
        )}
      </Tag>
    );
  };

const MarkdownContent: React.FC<MarkdownContentProps> = ({ markdown, baseSlug, headingAnchors = [], diagrams = [], className }) => {
  const docsBase = useDocsBasePath();
  const { organizationId } = useParams<{ organizationId?: string }>();

  const components = useMemo<Components>(() => ({
    h2: HeadingWithAnchor("h2"),
    h3: HeadingWithAnchor("h3"),
    h4: HeadingWithAnchor("h4"),
    a: ({ href, children }) => {
      const resolved = resolveDocsLink(href ?? "", baseSlug, docsBase, organizationId);
      switch (resolved.kind) {
        case "internal":
          return <Link to={resolved.href}>{children}</Link>;
        case "anchor":
          return <a href={resolved.href}>{children}</a>;
        case "external":
          return <a href={resolved.href} target="_blank" rel="noreferrer">{children}</a>;
        case "unavailable":
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help underline decoration-dotted underline-offset-4">{children}</span>
              </TooltipTrigger>
              <TooltipContent>{resolved.reason}</TooltipContent>
            </Tooltip>
          );
      }
    },
    blockquote: ({ node, children }) => {
      const type = (node?.properties?.dataAlert as AlertType | undefined) ?? undefined;
      if (!type || !ALERTS[type]) return <blockquote>{children}</blockquote>;
      const alert = ALERTS[type];
      const Icon = alert.icon;
      // Not `not-prose`: the typography plugin excludes every descendant of a not-prose element, so links and code
      // inside the callout would lose their styling. The callout's own margins are reset instead.
      return (
        <div role="note" className={cn("my-4 rounded-md border-l-4 border px-4 py-3", alert.className)}>
          <div className="docs-alert-title mb-1 flex items-center gap-2 text-sm font-semibold">
            <Icon className="size-4" />
            {alert.label}
          </div>
          <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">{children}</div>
        </div>
      );
    },
    pre: ({ node, children }) => {
      const index = node?.properties?.dataDiagramIndex;
      if (index !== undefined) {
        const diagram = diagrams[Number(index)];
        return diagram ? <DiagramFigure diagram={diagram} /> : null;
      }
      const text = node ? node.children.map(textOf).join("") : "";
      return <CodeBlock singleLine={!text.trimEnd().includes("\n")}>{children}</CodeBlock>;
    },
    table: ({ children }) => (
      <div className="not-prose my-4 overflow-x-auto rounded-md border">
        <table className="w-full text-sm [&_th]:bg-muted/60 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border-t [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs">
          {children}
        </table>
      </div>
    ),
  }), [baseSlug, docsBase, organizationId, diagrams]);

  const rehypePlugins = useMemo(() => [rehypeAlerts, rehypeDiagramIndex, () => rehypeHeadingIds(headingAnchors)], [headingAnchors]);

  return (
    <div className={cn("docs-prose prose prose-sm max-w-none", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins} components={components}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
